import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Search, Edit, Trash2, Package, Upload, Image as ImageIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { useInventory } from '../hooks/useInventory';
import ConfirmPopup from './ui/ConfirmPopup';
import AddProductModal from './AddProductModal';

interface Product {
  id: string;
  name: string;
  category: string;
  stock: number;
  priceCost: number;
  priceSale: number;
  brand: string;
  supplier: string;
  sku: string;
  fechaIngreso: string;
  imageURL?: string;
  data: Record<string, any>;
  createdAt: any;
  updatedAt: any;
  variants?: any[]; // Agregar variantes para compatibilidad con la estructura documentada
}

interface ProductVariant {
  id: string;
  color: string;
  model?: string;
  isMain: boolean;
  mainImageType: 'frontal' | 'reverso' | null;
  images: {
    frontal: File | null;
    reverso: File | null;
    closeup1: File | null;
    closeup2: File | null;
    closeup3: File | null;
    closeup4: File | null;
  };
  // URLs de imágenes existentes (para edición)
  existingImages?: {
    frontal?: string;
    reverso?: string;
    closeup1?: string;
    closeup2?: string;
    closeup3?: string;
    closeup4?: string;
  };
}

interface Brand {
  id: string;
  name: string;
  description: string;
  createdAt: string;
}

interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  createdAt: string;
}

interface ProductManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProductManagementModal: React.FC<ProductManagementModalProps> = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { products, brands, suppliers, categories, loading, error, loadProducts, loadBrands, loadSuppliers, loadCategories, deleteProduct: deleteProductFromHook, updateProductWithVariants, uploadProductImage } = useInventory();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | null>(null);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([
    { 
      id: '1', 
      color: '', 
      model: '', 
      isMain: true, 
      mainImageType: null,
      images: { 
        frontal: null, 
        reverso: null, 
        closeup1: null, 
        closeup2: null, 
        closeup3: null, 
        closeup4: null 
      } 
    }
  ]);

  // Formulario para nuevo producto
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    stock: 0,
    priceCost: 0,
    priceSale: 0,
    brand: '',
    supplier: '',
    imageURL: '',
    data: {} as Record<string, any>
  });

  // Usar todas las categorías cargadas desde la base de datos
  const allCategories = categories.map(cat => cat.name);
  
  // Debug: Log de categorías para diagnóstico
  useEffect(() => {
    console.log('🔍 ProductManagementModal - Categorías cargadas:', categories.length);
    console.log('🔍 ProductManagementModal - Todas las categorías:', allCategories.length);
    console.log('🔍 ProductManagementModal - Lista de todas las categorías:', allCategories);
  }, [categories, allCategories]);

  useEffect(() => {
    console.log('🔍 ProductManagementModal - Productos cargados:', products.length);
    console.log('🔍 ProductManagementModal - Estado de carga:', loading);
    console.log('🔍 ProductManagementModal - Error:', error);
    if (products.length > 0) {
      console.log('📋 ProductManagementModal - Primeros 3 productos:', products.slice(0, 3).map(p => ({
        id: p.id,
        name: p.name,
        category: p.category,
        imageURL: p.imageURL,
        sku: p.sku
      })));
    }
  }, [products, loading, error]);

  useEffect(() => {
    console.log('🔍 Estado showAddForm cambió:', showAddForm);
  }, [showAddForm]);

  useEffect(() => {
    if (isOpen && user) {
      console.log('🔄 Abriendo ProductManagementModal, cargando datos...');
      loadData();
    }
  }, [isOpen, user]);

  // Efecto adicional para asegurar que las categorías se carguen
  useEffect(() => {
    if (isOpen && user && categories.length === 0) {
      console.log('⚠️ No hay categorías cargadas, intentando cargar nuevamente...');
      setTimeout(() => {
        loadCategories();
      }, 1000);
    }
  }, [isOpen, user, categories.length]);

  // Manejar tecla ESC para cerrar modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleCloseModal();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const handleCloseModal = () => {
    // Resetear estado al cerrar
    setShowAddForm(false);
    setEditingProduct(null);
    setFormData({
      name: '', category: '', stock: 0, priceCost: 0, priceSale: 0, 
      brand: '', supplier: '', imageURL: '', data: {}
    });
    setVariants([{
      id: '1', 
      color: '', 
      model: '', 
      isMain: true, 
      mainImageType: null,
      images: { 
        frontal: null, 
        reverso: null, 
        closeup1: null, 
        closeup2: null, 
        closeup3: null, 
        closeup4: null 
      }
    }]);
    onClose();
  };

  const loadData = async () => {
    if (!user) return;
    try {
      console.log('🔄 Cargando datos en ProductManagementModal...');
      // Cargar datos usando el hook useInventory
      await Promise.all([
        loadProducts(),
        loadBrands(),
        loadSuppliers(),
        loadCategories()
      ]);
      console.log('✅ Datos cargados en ProductManagementModal');
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
    }
  };

  // Funciones para manejar variantes
  const addVariant = () => {
    if (variants.length >= 6) {
      alert('Máximo 6 variantes por producto');
      return;
    }
    
    const newVariant: ProductVariant = {
      id: Date.now().toString(),
      color: '',
      model: '',
      isMain: false,
      mainImageType: null,
      images: { 
        frontal: null, 
        reverso: null, 
        closeup1: null, 
        closeup2: null, 
        closeup3: null, 
        closeup4: null 
      }
    };
    setVariants(prev => [...prev, newVariant]);
  };

  const removeVariant = (variantId: string) => {
    if (variants.length > 1) {
      setVariants(prev => prev.filter(v => v.id !== variantId));
    }
  };

  const updateVariant = (variantId: string, field: 'color' | 'model', value: string) => {
    setVariants(prev => prev.map(v => 
      v.id === variantId ? { ...v, [field]: value } : v
    ));
  };

  const handleImageSelect = (variantId: string, imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3' | 'closeup4', file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      alert('La imagen no puede superar los 5MB');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      alert('Solo se permiten archivos de imagen');
      return;
    }

    setVariants(prev => prev.map(v => 
      v.id === variantId 
        ? { ...v, images: { ...v.images, [imageType]: file } }
        : v
    ));
  };

  const removeImage = (variantId: string, imageType: 'frontal' | 'reverso' | 'closeup1' | 'closeup2' | 'closeup3' | 'closeup4') => {
    setVariants(prev => prev.map(v => 
      v.id === variantId 
        ? { 
            ...v, 
            images: { ...v.images, [imageType]: null },
            existingImages: { ...v.existingImages, [imageType]: '' }
          }
        : v
    ));
  };

  const setMainImage = (variantId: string, imageType: 'frontal' | 'reverso') => {
    setVariants(prev => prev.map(v => 
      v.id === variantId 
        ? { ...v, mainImageType: imageType }
        : v
    ));
  };

  const validateVariants = () => {
    for (const variant of variants) {
      if (!variant.color.trim()) {
        alert(`La variante ${variants.indexOf(variant) + 1} debe tener un color`);
        return false;
      }
      
      // Verificar que tenga al menos una imagen principal (frontal o reverso)
      const hasFrontal = variant.images.frontal || variant.existingImages?.frontal;
      const hasReverso = variant.images.reverso || variant.existingImages?.reverso;
      
      if (!hasFrontal && !hasReverso) {
        alert(`La variante "${variant.color}" debe tener al menos una imagen principal (frontal o reverso)`);
        return false;
      }
      
      // Verificar que se haya establecido una imagen principal
      if (!variant.mainImageType) {
        alert(`La variante "${variant.color}" debe tener una imagen principal seleccionada`);
        return false;
      }
      
      // Verificar que la imagen principal seleccionada exista
      const mainImageExists = variant.images[variant.mainImageType] || variant.existingImages?.[variant.mainImageType];
      if (variant.mainImageType && !mainImageExists) {
        alert(`La imagen principal seleccionada para "${variant.color}" no existe`);
        return false;
      }
    }
    return true;
  };

  const handleAddProduct = async () => {
    if (!user) return;
    
    // Validar formulario básico
    if (!formData.name.trim()) {
      alert('El nombre del producto es obligatorio');
      return;
    }
    
    if (!formData.category) {
      alert('La categoría es obligatoria');
      return;
    }
    
    // Validar variantes
    if (!validateVariants()) {
      return;
    }
    
    try {
      if (editingProduct) {
        // Editar producto existente con variantes e imágenes
        console.log('🔄 Editando producto con variantes:', editingProduct.sku);
        
        // Preparar datos del producto
        const productData = {
          name: formData.name,
          category: formData.category,
          stock: formData.stock,
          precioCompra: formData.priceCost,
          precioVenta: formData.priceSale,
          marca: formData.brand,
          proveedor: formData.supplier,
          description: formData.data.description || '',
          fechaIngreso: editingProduct.fechaIngreso || new Date().toISOString().split('T')[0],
          data: formData.data
        };
        
        // Procesar variantes con imágenes
        const processedVariants = await Promise.all(
          variants.map(async (variant) => {
            const variantData: any = {
              id: variant.id,
              color: variant.color,
              model: variant.model,
              isMain: variant.isMain,
              mainImage: variant.mainImageType === 'frontal' ? 'front' : 
                        variant.mainImageType === 'reverso' ? 'reverse' : 
                        variant.mainImageType,
              images: {}
            };
            
            // Procesar cada tipo de imagen
            const imageTypes = ['frontal', 'reverso', 'closeup1', 'closeup2', 'closeup3', 'closeup4'] as const;
            
            for (const imageType of imageTypes) {
              if (variant.images[imageType]) {
                // Nueva imagen subida
                console.log(`📤 Subiendo imagen ${imageType} para variante ${variant.color}`);
                const uploadResult = await uploadProductImage(
                  variant.images[imageType]!,
                  editingProduct.sku,
                  formData.category,
                  variant.color,
                  imageType
                );
                
                if (uploadResult.success) {
                  // Mapear a la estructura de Firestore
                  const firestoreImageType = imageType === 'frontal' ? 'front' : 
                                           imageType === 'reverso' ? 'reverse' : 
                                           imageType;
                  variantData.images[firestoreImageType] = uploadResult.url;
                  console.log(`✅ Imagen ${imageType} subida como ${firestoreImageType}:`, uploadResult.url);
                } else {
                  console.error(`❌ Error subiendo imagen ${imageType}:`, uploadResult.error);
                  // Mantener imagen existente si hay error
                  const firestoreImageType = imageType === 'frontal' ? 'front' : 
                                           imageType === 'reverso' ? 'reverse' : 
                                           imageType;
                  variantData.images[firestoreImageType] = variant.existingImages?.[imageType] || '';
                }
              } else if (variant.existingImages?.[imageType]) {
                // Mantener imagen existente
                const firestoreImageType = imageType === 'frontal' ? 'front' : 
                                         imageType === 'reverso' ? 'reverse' : 
                                         imageType;
                variantData.images[firestoreImageType] = variant.existingImages[imageType];
                console.log(`🔄 Manteniendo imagen existente ${imageType} como ${firestoreImageType}:`, variant.existingImages[imageType]);
              }
            }
            
            return variantData;
          })
        );
        
        // Actualizar producto con variantes
        await updateProductWithVariants(
          editingProduct.sku,
          formData.category,
          productData,
          processedVariants
        );
        
        console.log('✅ Producto editado con variantes:', editingProduct.name);
        console.log('📋 Datos del producto guardados:', productData);
        console.log('🖼️ Variantes procesadas:', processedVariants);
        setEditingProduct(null);
      } else {
        // Agregar nuevo producto (lógica existente)
        const newProduct: Product = {
          id: Date.now().toString(),
          name: formData.name,
          category: formData.category,
          stock: formData.stock,
          priceCost: formData.priceCost,
          priceSale: formData.priceSale,
          brand: formData.brand,
          supplier: formData.supplier,
          sku: `${formData.category.substring(0, 3).toUpperCase()}${Date.now().toString().slice(-3)}`,
          fechaIngreso: new Date().toISOString().split('T')[0],
          imageURL: formData.imageURL,
          data: formData.data,
          createdAt: new Date().toISOString().split('T')[0],
          updatedAt: new Date().toISOString().split('T')[0]
        };

        // Recargar productos después de agregar
        await loadProducts();
        console.log('✅ Producto agregado:', newProduct.name);
      }

      // Limpiar formulario
      setFormData({
        name: '', category: '', stock: 0, priceCost: 0, priceSale: 0, 
        brand: '', supplier: '', imageURL: '', data: {}
      });
      setVariants([{
        id: '1', 
        color: '', 
        model: '', 
        isMain: true, 
        mainImageType: null,
        images: { 
          frontal: null, 
          reverso: null, 
          closeup1: null, 
          closeup2: null, 
          closeup3: null, 
          closeup4: null 
        }
      }]);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error agregando/editando producto:', error);
      alert('Error al guardar el producto. Por favor, inténtalo de nuevo.');
    }
  };


  const handleEditProduct = (product: Product) => {
    console.log('🔄 Editando producto:', product);
    console.log('🔍 Producto completo:', JSON.stringify(product, null, 2));
    
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category: product.category,
      stock: product.stock,
      priceCost: product.priceCost,
      priceSale: product.priceSale,
      brand: product.brand,
      supplier: product.supplier,
      imageURL: product.imageURL || '',
      data: product.data
    });
    
    // Cargar variantes del producto si existen
    if (product.variants && product.variants.length > 0) {
      console.log('📋 Cargando variantes del producto:', product.variants);
      
      // Convertir las variantes del producto al formato del modal
      const loadedVariants: ProductVariant[] = product.variants.map((variant: any, index: number) => {
        console.log(`🔍 Procesando variante ${index}:`, variant);
        console.log(`🖼️ Imágenes de la variante:`, variant.images);
        
        const processedVariant = {
          id: variant.id || `variant-${index}`,
          color: variant.color || '',
          model: variant.model || variant.size || '',
          isMain: variant.isMain || index === 0,
          mainImageType: variant.mainImage === 'front' ? 'frontal' : 
                        variant.mainImage === 'reverse' ? 'reverso' :
                        variant.mainImageType || 
                        (variant.images?.front ? 'frontal' : variant.images?.reverse ? 'reverso' : null),
          images: {
            frontal: null, // Las imágenes existentes se mostrarán como URLs
            reverso: null,
            closeup1: null,
            closeup2: null,
            closeup3: null,
            closeup4: null
          },
          existingImages: {
            frontal: variant.images?.front || variant.images?.frontal || '',
            reverso: variant.images?.reverse || variant.images?.reverso || '',
            closeup1: variant.images?.closeup1 || '',
            closeup2: variant.images?.closeup2 || '',
            closeup3: variant.images?.closeup3 || '',
            closeup4: variant.images?.closeup4 || ''
          }
        };
        
        console.log(`🔍 Variante ${index} - Imágenes originales:`, {
          front: variant.images?.front,
          reverse: variant.images?.reverse,
          frontal: variant.images?.frontal,
          reverso: variant.images?.reverso,
          closeup1: variant.images?.closeup1,
          closeup2: variant.images?.closeup2,
          closeup3: variant.images?.closeup3,
          closeup4: variant.images?.closeup4
        });
        
        console.log(`🔍 Variante ${index} - Imágenes procesadas:`, processedVariant.existingImages);
        
        console.log(`✅ Variante procesada:`, processedVariant);
        console.log(`🖼️ Imágenes existentes:`, processedVariant.existingImages);
        
        return processedVariant;
      });
      
      console.log('📋 Variantes cargadas:', loadedVariants);
      setVariants(loadedVariants);
    } else {
      // Si no hay variantes, crear una por defecto
      setVariants([{
        id: '1', 
        color: '', 
        model: '', 
        isMain: true, 
        mainImageType: null,
        images: { 
          frontal: null, 
          reverso: null, 
          closeup1: null, 
          closeup2: null, 
          closeup3: null, 
          closeup4: null 
        }
      }]);
    }
    
    setShowAddForm(true);
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setFormData({
      name: '', category: '', stock: 0, priceCost: 0, priceSale: 0, 
      brand: '', supplier: '', imageURL: '', data: {}
    });
    setShowAddForm(false);
  };

  const handleDeleteProduct = (productId: string) => {
    setProductToDelete(productId);
    setShowConfirmPopup(true);
  };

  const handleConfirmDelete = async () => {
    if (!productToDelete) return;
    
    try {
      // Eliminar de Firebase usando la función del hook
      await deleteProductFromHook(productToDelete);
      console.log(`✅ Producto eliminado de Firebase: ${productToDelete}`);
    } catch (error) {
      console.error('Error eliminando producto:', error);
    } finally {
      setProductToDelete(null);
      setShowConfirmPopup(false);
    }
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = (product.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (product.sku || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
            onClick={handleCloseModal}
            className="fixed inset-0 bg-black/50 z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className={`fixed inset-4 z-50 ${
              isDark ? 'bg-slate-900' : 'bg-white'
            } rounded-2xl shadow-2xl overflow-hidden`}
          >
            <div className="flex flex-col h-full">
            {/* Header */}
              <div className={`flex items-center justify-between p-6 border-b ${
                isDark ? 'border-slate-700' : 'border-gray-200'
              }`}>
              <div className="flex items-center space-x-3">
                  <Package size={24} className="text-blue-500" />
                  <h2 className={`text-2xl font-bold ${
                    isDark ? 'text-white' : 'text-gray-900'
                  }`}>
                    {t('productManagement')}
                </h2>
              </div>
              <button
                onClick={handleCloseModal}
                  className={`p-2 rounded-xl transition-colors duration-200 ${
                    isDark 
                      ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
              >
                <X size={24} />
              </button>
            </div>

            {/* Content */}
              <div className="flex-1 overflow-hidden">
                {!showAddForm ? (
                  // Vista principal
                  <div className="h-full flex flex-col">
                    {/* Header con controles */}
                    <div className={`p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex flex-col space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className={`text-lg font-semibold ${
                              isDark ? 'text-white' : 'text-gray-900'
                            }`}>
                              {t('productInventory')}
                            </h3>
                            <p className={`text-sm ${
                              isDark ? 'text-gray-400' : 'text-gray-600'
                            }`}>
                              {t('manageProductCatalog')}
                            </p>
                          </div>
                          <button
                            onClick={() => setShowAddProductModal(true)}
                            className="btn-primary flex items-center space-x-2 px-6 py-2"
                          >
                            <Plus size={20} />
                            <span>{t('addProduct')}</span>
                          </button>
                        </div>

                        {/* Controles de búsqueda y filtro */}
                        <div className="flex flex-col sm:flex-row gap-4">
                          <div className="flex-1">
                            <div className="relative">
                              <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                                isDark ? 'text-gray-400' : 'text-gray-500'
                              }`} />
                              <input
                                type="text"
                                placeholder={t('searchProducts')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`w-full pl-10 pr-4 py-2 rounded-xl border ${
                                  isDark 
                                    ? 'bg-slate-800 border-slate-700 text-white' 
                                    : 'bg-white border-gray-300 text-gray-900'
                                } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              />
                            </div>
                          </div>
                          <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className={`px-4 py-2 rounded-xl border ${
                              isDark 
                                ? 'bg-slate-800 border-slate-700 text-white' 
                                : 'bg-white border-gray-300 text-gray-900'
                            } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                          >
                            <option value="">{t('allCategories')}</option>
                            {allCategories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Lista de productos */}
                    <div className="flex-1 overflow-y-auto p-6">
                      {loading ? (
                        <div className="flex items-center justify-center h-64">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                        </div>
                      ) : filteredProducts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-center">
                          <Package size={64} className={`mb-4 ${
                            isDark ? 'text-gray-600' : 'text-gray-400'
                          }`} />
                          <h3 className={`text-lg font-medium mb-2 ${
                            isDark ? 'text-gray-300' : 'text-gray-600'
                          }`}>
                            {searchTerm ? t('noProductsFound') : 
                             selectedCategory ? `No hay productos en ${selectedCategory}` : 
                             'No hay productos'}
                          </h3>
                          <p className={`text-sm ${
                            isDark ? 'text-gray-500' : 'text-gray-500'
                          }`}>
                            {searchTerm ? 'Intenta con otros términos de búsqueda' : 
                             selectedCategory ? 'Esta categoría está vacía' : 
                             'Agrega tu primer producto'}
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                          {filteredProducts.map((product) => (
                            <div
                              key={product.id}
                              className={`p-6 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700' 
                                  : 'bg-white border-gray-200'
                              } shadow-sm hover:shadow-md transition-shadow duration-200`}
                            >
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <h3 className={`text-lg font-semibold ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {product.name}
                                  </h3>
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    SKU: {product.sku}
                                  </p>
                                  <p className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Categoría: {product.category}
                                  </p>
                                </div>
                                <div className="flex space-x-2">
                                  <button 
                                    onClick={() => handleEditProduct(product)}
                                    className={`p-2 rounded-lg transition-colors duration-200 ${
                                      isDark 
                                        ? 'hover:bg-slate-700 text-gray-400 hover:text-white' 
                                        : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                                    }`}
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteProduct(product.id)}
                                    className={`p-2 rounded-lg transition-colors duration-200 ${
                                      isDark 
                                        ? 'hover:bg-slate-700 text-gray-400 hover:text-red-400' 
                                        : 'hover:bg-gray-100 text-gray-500 hover:text-red-600'
                                    }`}
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                              
                              {/* Imagen del producto */}
                              {product.imageURL && (
                                <div className="mb-4">
                                  <img 
                                    src={product.imageURL} 
                                    alt={product.name}
                                    className="w-full h-32 object-cover rounded-lg"
                                    onError={(e) => {
                                      const target = e.currentTarget as HTMLImageElement;
                                      target.style.display = 'none';
                                    }}
                                  />
                                </div>
                              )}
                              
                              <div className="space-y-2">
                                <div className="flex justify-between">
                                  <span className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Stock:
                                  </span>
                                  <span className={`text-sm font-medium ${
                                    product.stock < 10 ? 'text-red-500' : 
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    {product.stock}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Precio Costo:
                                  </span>
                                  <span className={`text-sm font-medium ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    ${product.priceCost}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className={`text-sm ${
                                    isDark ? 'text-gray-400' : 'text-gray-600'
                                  }`}>
                                    Precio Venta:
                                  </span>
                                  <span className={`text-sm font-medium ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    ${product.priceSale}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  // Formulario de nuevo producto
                  <div className="h-full flex flex-col">
                    {/* Header del formulario */}
                    <div className={`flex items-center justify-between p-6 border-b ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <h3 className={`text-xl font-bold ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>
                        {editingProduct ? t('editProduct') : t('addProduct')}
                      </h3>
                      <button
                        onClick={handleCancelEdit}
                        className={`p-2 rounded-xl transition-colors duration-200 ${
                          isDark 
                            ? 'hover:bg-slate-800 text-gray-300 hover:text-white' 
                            : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <X size={24} />
                      </button>
                    </div>

                    {/* Formulario */}
                    <div className="flex-1 overflow-y-auto p-6">
                      <div className="max-w-2xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="md:col-span-2">
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Nombre del Producto
                            </label>
                            <input
                              type="text"
                              value={formData.name}
                              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder={t('productNamePlaceholder')}
                            />
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Categoría
                            </label>
                            <select
                              value={formData.category}
                              onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            >
                              <option value="">{t('selectCategory')}</option>
                              {allCategories.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Stock
                            </label>
                            <input
                              type="number"
                              value={formData.stock}
                              onChange={(e) => setFormData(prev => ({ ...prev, stock: parseInt(e.target.value) || 0 }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Precio Costo
                            </label>
                            <input
                              type="number"
                              value={formData.priceCost}
                              onChange={(e) => setFormData(prev => ({ ...prev, priceCost: parseFloat(e.target.value) || 0 }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Precio Venta
                            </label>
                            <input
                              type="number"
                              value={formData.priceSale}
                              onChange={(e) => setFormData(prev => ({ ...prev, priceSale: parseFloat(e.target.value) || 0 }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder="0"
                            />
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Marca
                            </label>
                            <input
                              type="text"
                              value={formData.brand}
                              onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                              list="brands-list"
                              placeholder={t('brandPlaceholder')}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            <datalist id="brands-list">
                              {brands.map(brand => (
                                <option key={brand.id} value={brand.name} />
                              ))}
                            </datalist>
                          </div>

                          <div>
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              Proveedor
                            </label>
                            <input
                              type="text"
                              value={formData.supplier}
                              onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
                              list="suppliers-list"
                              placeholder={t('supplierPlaceholder')}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                            />
                            <datalist id="suppliers-list">
                              {suppliers.map(supplier => (
                                <option key={supplier.id} value={supplier.name} />
                              ))}
                            </datalist>
                          </div>

                          <div className="md:col-span-2">
                            <label className={`block text-sm font-medium mb-2 ${
                              isDark ? 'text-gray-300' : 'text-gray-700'
                            }`}>
                              URL de Imagen
                            </label>
                            <input
                              type="url"
                              value={formData.imageURL}
                              onChange={(e) => setFormData(prev => ({ ...prev, imageURL: e.target.value }))}
                              className={`w-full px-4 py-2 rounded-xl border ${
                                isDark 
                                  ? 'bg-slate-800 border-slate-700 text-white' 
                                  : 'bg-white border-gray-300 text-gray-900'
                              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                              placeholder="https://ejemplo.com/imagen.jpg"
                            />
                          </div>
                        </div>

                        {/* Sección de Variantes */}
                        <div className="mt-8">
                          <div className="flex items-center justify-between mb-6">
                            <div>
                              <h3 className={`text-lg font-semibold ${
                                isDark ? 'text-white' : 'text-gray-900'
                              }`}>
                                {t('variants')}
                              </h3>
                              <p className={`text-sm ${
                                isDark ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {t('addVariantDescription')}
                              </p>
                            </div>
                            <button
                              onClick={addVariant}
                              disabled={variants.length >= 6}
                              className={`btn-primary flex items-center space-x-2 px-4 py-2 ${
                                variants.length >= 6 ? 'opacity-50 cursor-not-allowed' : ''
                              }`}
                            >
                              <Plus size={16} />
                              <span>{t('addVariant')}</span>
                            </button>
                          </div>

                          <div className="space-y-6">
                            {variants.map((variant, index) => (
                              <div
                                key={variant.id}
                                className={`p-6 rounded-xl border ${
                                  isDark 
                                    ? 'bg-slate-800 border-slate-700' 
                                    : 'bg-white border-gray-200'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-4">
                                  <h4 className={`text-md font-medium ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                  }`}>
                                    Variante {index + 1}
                                  </h4>
                                  {variants.length > 1 && (
                                    <button
                                      onClick={() => removeVariant(variant.id)}
                                      className={`p-2 rounded-lg transition-colors duration-200 ${
                                        isDark 
                                          ? 'hover:bg-slate-700 text-gray-400 hover:text-red-400' 
                                          : 'hover:bg-gray-100 text-gray-600 hover:text-red-600'
                                      }`}
                                    >
                                      <Trash2 size={16} />
                                    </button>
                                  )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                  <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                      isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      Color *
                                    </label>
                                    <input
                                      type="text"
                                      value={variant.color}
                                      onChange={(e) => updateVariant(variant.id, 'color', e.target.value)}
                                      className={`w-full px-4 py-2 rounded-xl border ${
                                        isDark 
                                          ? 'bg-slate-800 border-slate-700 text-white' 
                                          : 'bg-white border-gray-300 text-gray-900'
                                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                      placeholder={t('colorPlaceholder')}
                                    />
                                  </div>
                                  <div>
                                    <label className={`block text-sm font-medium mb-2 ${
                                      isDark ? 'text-gray-300' : 'text-gray-700'
                                    }`}>
                                      Modelo
                                    </label>
                                    <input
                                      type="text"
                                      value={variant.model || ''}
                                      onChange={(e) => updateVariant(variant.id, 'model', e.target.value)}
                                      className={`w-full px-4 py-2 rounded-xl border ${
                                        isDark 
                                          ? 'bg-slate-800 border-slate-700 text-white' 
                                          : 'bg-white border-gray-300 text-gray-900'
                                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                                      placeholder={t('modelPlaceholder')}
                                    />
                                  </div>
                                </div>

                                {/* Sección de Imágenes */}
                                <div>
                                  <h5 className={`text-sm font-medium mb-4 ${
                                    isDark ? 'text-gray-300' : 'text-gray-700'
                                  }`}>
                                    {t('images')}
                                  </h5>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {/* Imagen Frontal */}
                                    <div className={`p-4 rounded-lg border-2 border-dashed ${
                                      isDark ? 'border-slate-600' : 'border-gray-300'
                                    }`}>
                                      <div className="text-center">
                                        <ImageIcon className={`mx-auto mb-2 ${
                                          isDark ? 'text-gray-500' : 'text-gray-400'
                                        }`} size={24} />
                                        <p className={`text-sm font-medium mb-2 ${
                                          isDark ? 'text-white' : 'text-gray-900'
                                        }`}>
                                          Imagen Frontal *
                                        </p>
                                        {(() => {
                                          console.log(`🖼️ Renderizando imagen frontal para variante ${variant.id}:`, {
                                            hasNewImage: !!variant.images.frontal,
                                            hasExistingImage: !!variant.existingImages?.frontal,
                                            existingImageUrl: variant.existingImages?.frontal
                                          });
                                          return null;
                                        })()}
                                        {(() => {
                                          const hasNewImage = !!variant.images.frontal;
                                          const hasExistingImage = !!variant.existingImages?.frontal;
                                          const shouldShowImage = hasNewImage || hasExistingImage;
                                          
                                          console.log(`🖼️ Renderizando imagen frontal para variante ${variant.id}:`, {
                                            hasNewImage,
                                            hasExistingImage,
                                            shouldShowImage,
                                            existingImageUrl: variant.existingImages?.frontal
                                          });
                                          
                                          return shouldShowImage;
                                        })() ? (
                                          <div className="space-y-2">
                                            <img
                                              src={variant.images.frontal ? URL.createObjectURL(variant.images.frontal) : variant.existingImages?.frontal}
                                              alt={t('frontalImage')}
                                              className={`w-full h-24 object-cover rounded-lg ${
                                                variant.mainImageType === 'frontal' ? 'ring-2 ring-blue-500' : ''
                                              }`}
                                              onError={(e) => {
                                                console.error('❌ Error cargando imagen frontal:', e);
                                                const target = e.currentTarget as HTMLImageElement;
                                                target.style.display = 'none';
                                              }}
                                              onLoad={() => {
                                                console.log('✅ Imagen frontal cargada exitosamente');
                                              }}
                                            />
                                            <div className="space-y-1">
                                              <button
                                                onClick={() => setMainImage(variant.id, 'frontal')}
                                                className={`w-full text-xs px-2 py-1 rounded ${
                                                  variant.mainImageType === 'frontal'
                                                    ? 'bg-blue-500 text-white'
                                                    : isDark 
                                                      ? 'bg-slate-600 hover:bg-slate-500 text-white' 
                                                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                                }`}
                                              >
                                                {variant.mainImageType === 'frontal' ? `✓ ${t('mainImage')}` : t('setAsMain')}
                                              </button>
                                              <button
                                                onClick={() => removeImage(variant.id, 'frontal')}
                                                className="text-red-500 text-xs hover:text-red-700"
                                              >
                                                {t('removeImage')}
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleImageSelect(variant.id, 'frontal', file);
                                              }}
                                              className="hidden"
                                              id={`frontal-${variant.id}`}
                                            />
                                            <label
                                              htmlFor={`frontal-${variant.id}`}
                                              className={`cursor-pointer inline-flex items-center px-3 py-2 rounded-lg text-sm ${
                                                isDark 
                                                  ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                              }`}
                                            >
                                              <Upload size={14} className="mr-2" />
                                              Subir Imagen
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Imagen Reverso */}
                                    <div className={`p-4 rounded-lg border-2 border-dashed ${
                                      isDark ? 'border-slate-600' : 'border-gray-300'
                                    }`}>
                                      <div className="text-center">
                                        <ImageIcon className={`mx-auto mb-2 ${
                                          isDark ? 'text-gray-500' : 'text-gray-400'
                                        }`} size={24} />
                                        <p className={`text-sm font-medium mb-2 ${
                                          isDark ? 'text-white' : 'text-gray-900'
                                        }`}>
                                          Imagen Reverso *
                                        </p>
                                        {(variant.images.reverso || variant.existingImages?.reverso) ? (
                                          <div className="space-y-2">
                                            <img
                                              src={variant.images.reverso ? URL.createObjectURL(variant.images.reverso) : variant.existingImages?.reverso}
                                              alt={t('backImage')}
                                              className={`w-full h-24 object-cover rounded-lg ${
                                                variant.mainImageType === 'reverso' ? 'ring-2 ring-blue-500' : ''
                                              }`}
                                            />
                                            <div className="space-y-1">
                                              <button
                                                onClick={() => setMainImage(variant.id, 'reverso')}
                                                className={`w-full text-xs px-2 py-1 rounded ${
                                                  variant.mainImageType === 'reverso'
                                                    ? 'bg-blue-500 text-white'
                                                    : isDark 
                                                      ? 'bg-slate-600 hover:bg-slate-500 text-white' 
                                                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                                                }`}
                                              >
                                                {variant.mainImageType === 'reverso' ? `✓ ${t('mainImage')}` : t('setAsMain')}
                                              </button>
                                              <button
                                                onClick={() => removeImage(variant.id, 'reverso')}
                                                className="text-red-500 text-xs hover:text-red-700"
                                              >
                                                {t('removeImage')}
                                              </button>
                                            </div>
                                          </div>
                                        ) : (
                                          <div>
                                            <input
                                              type="file"
                                              accept="image/*"
                                              onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) handleImageSelect(variant.id, 'reverso', file);
                                              }}
                                              className="hidden"
                                              id={`reverso-${variant.id}`}
                                            />
                                            <label
                                              htmlFor={`reverso-${variant.id}`}
                                              className={`cursor-pointer inline-flex items-center px-3 py-2 rounded-lg text-sm ${
                                                isDark 
                                                  ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                                                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                              }`}
                                            >
                                              <Upload size={14} className="mr-2" />
                                              Subir Imagen
                                            </label>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Close-ups */}
                                    {['closeup1', 'closeup2', 'closeup3', 'closeup4'].map((closeupType) => (
                                      <div key={closeupType} className={`p-4 rounded-lg border-2 border-dashed ${
                                        isDark ? 'border-slate-600' : 'border-gray-300'
                                      }`}>
                                        <div className="text-center">
                                          <ImageIcon className={`mx-auto mb-2 ${
                                            isDark ? 'text-gray-500' : 'text-gray-400'
                                          }`} size={24} />
                                          <p className={`text-sm font-medium mb-2 ${
                                            isDark ? 'text-white' : 'text-gray-900'
                                          }`}>
                                            Close-up {closeupType.slice(-1)}
                                          </p>
                                          {(variant.images[closeupType as keyof typeof variant.images] || variant.existingImages?.[closeupType as keyof typeof variant.existingImages]) ? (
                                            <div className="space-y-2">
                                              <img
                                                src={variant.images[closeupType as keyof typeof variant.images] 
                                                  ? URL.createObjectURL(variant.images[closeupType as keyof typeof variant.images] as File)
                                                  : variant.existingImages?.[closeupType as keyof typeof variant.existingImages]
                                                }
                                                alt={`${t('closeup')} ${closeupType.slice(-1)}`}
                                                className="w-full h-24 object-cover rounded-lg"
                                              />
                                              <button
                                                onClick={() => removeImage(variant.id, closeupType as any)}
                                                className="text-red-500 text-xs hover:text-red-700"
                                              >
                                                {t('removeImage')}
                                              </button>
                                            </div>
                                          ) : (
                                            <div>
                                              <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => {
                                                  const file = e.target.files?.[0];
                                                  if (file) handleImageSelect(variant.id, closeupType as any, file);
                                                }}
                                                className="hidden"
                                                id={`${closeupType}-${variant.id}`}
                                              />
                                              <label
                                                htmlFor={`${closeupType}-${variant.id}`}
                                                className={`cursor-pointer inline-flex items-center px-3 py-2 rounded-lg text-sm ${
                                                  isDark 
                                                    ? 'bg-slate-700 hover:bg-slate-600 text-white' 
                                                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                                }`}
                                              >
                                                <Upload size={14} className="mr-2" />
                                                Subir Imagen
                                              </label>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
              </div>

                    {/* Footer del formulario */}
                    <div className={`p-6 border-t ${
                      isDark ? 'border-slate-700' : 'border-gray-200'
                    }`}>
                      <div className="flex justify-end space-x-4">
                        <button
                          onClick={handleCancelEdit}
                          className="btn-secondary px-6 py-2"
                        >
                          {t('cancel')}
                        </button>
                        <button
                          onClick={handleAddProduct}
                          className="btn-primary px-6 py-2"
                        >
                          {editingProduct ? t('saveProduct') : t('addProduct')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Pop-up de confirmación */}
          <ConfirmPopup
            isOpen={showConfirmPopup}
            onClose={() => {
              setShowConfirmPopup(false);
              setProductToDelete(null);
            }}
            onConfirm={handleConfirmDelete}
            title={t('deleteProduct')}
            message="¿Estás seguro de que quieres eliminar este producto?\n\nEsta acción no se puede deshacer."
            type="danger"
            confirmText={t('delete')}
            cancelText={t('cancel')}
          />

          {/* Modal de Agregar Producto */}
          <AddProductModal
            isOpen={showAddProductModal}
            onClose={() => {
              setShowAddProductModal(false);
              loadProducts(); // Recargar productos después de agregar
            }}
          />
        </>
      )}
    </AnimatePresence>
  );
};

export default ProductManagementModal;